import { SecureApiService } from '../secureApiService';
import { 
    AnalystOption,
    FlightChangeCaseRawResponse,
    FlightChangeDetail,
    FlightChangeDetailRawResponse,
    GetFlightsParams,
    GetFlightsResult,
    ReasonCode,
    UpdateFlightCasePayload,
    UpdateFlightCaseResponse
} from './types';
import { FlightChangeCaseMapper, FlightChangeDetailMapper } from './mappers';
import { FlightErrorHandler } from './errors';
import { X_TOTAL_ELEMENTS_HEADER } from './constants';

const API_BASE = '/flight/api/v1';
const FLIGHT_CHANGE_CASES_ENDPOINT = '/flight-change-cases';
const ANALYSTS_ENDPOINT = '/analysts';

export interface IFlightService {
    getFlights(params: GetFlightsParams): Promise<GetFlightsResult>;
    getAnalysts(): Promise<AnalystOption[]>;
    getFlightCaseDetailById(id: string): Promise<FlightChangeDetail>;
    getReasonCodes(): Promise<ReasonCode[]>;
    updateFlightCase(id: string, payload: UpdateFlightCasePayload): Promise<UpdateFlightCaseResponse>;
    assignFlightCase(id: string): Promise<void>;
}

export class FlightService implements IFlightService {
    private readonly flightChangeDetailMapper: FlightChangeDetailMapper;

    constructor(
        private readonly apiService: SecureApiService,
        private readonly flightChangeCaseMapper: FlightChangeCaseMapper,
        private readonly flightChangeCaseErrorHandler: FlightErrorHandler,
        private readonly baseUrl: string,
    ) {
        this.apiService.setBaseUrl('');
        this.flightChangeDetailMapper = new FlightChangeDetailMapper();
    }

    async getFlights(params: GetFlightsParams): Promise<GetFlightsResult> {
        try {
            const {
                page,
                count,
                sortBy = 'loadDate',
                sortOrder = 'desc',
                statusCodes,
                awb,
                analystId,
                isUnassigned,
            } = params;

            const searchParams = new URLSearchParams({
                page: String(page),
                count: String(count),
                sortBy,
                sortOrder,
            });
            if (statusCodes?.length) {
                searchParams.set('status', statusCodes.join(','));
            }
            if (awb != null && awb !== '') {
                searchParams.set('awb', awb);
            }

            if (isUnassigned === true) {
                searchParams.set('isUnassigned', 'true');
            } else if (analystId != null && !Number.isNaN(analystId)) {
                searchParams.set('analystId', String(analystId));
            }
            const url = `${API_BASE}${FLIGHT_CHANGE_CASES_ENDPOINT}?${searchParams.toString()}`;
            const response = await this.apiService.get<FlightChangeCaseRawResponse[]>(
                url,
                { requireAuth: true }
            );

            const totalHeader = response.headers.get(X_TOTAL_ELEMENTS_HEADER);
            const parsed = totalHeader && totalHeader.trim() !== ''
                ? Number.parseInt(totalHeader, 10)
                : Number.NaN;
            const fallbackTotal = response.data?.length ?? 0;
            const total = Number.isNaN(parsed) || parsed < 0 ? fallbackTotal : parsed;

            const data = this.flightChangeCaseMapper.mapArray(response.data);
            return { data, total };
        } catch (error) {
            this.flightChangeCaseErrorHandler.handle(error);
            throw error;
        }
    }

    async getAnalysts(): Promise<AnalystOption[]> {
        try {
            const response = await this.apiService.get<AnalystOption[]>(
                `${API_BASE}${ANALYSTS_ENDPOINT}`,
                { requireAuth: true },
            );
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            this.flightChangeCaseErrorHandler.handle(error);
            throw error;
        }
    }

    async getFlightCaseDetailById(id: string): Promise<FlightChangeDetail> {
        try {
            const response = await this.apiService.get<FlightChangeDetailRawResponse>(
                `${API_BASE}${FLIGHT_CHANGE_CASES_ENDPOINT}/${id}`, 
                { requireAuth: true }
            );
            
            return this.flightChangeDetailMapper.mapSingle(response.data);
        } catch (error) {
            this.flightChangeCaseErrorHandler.handle(error);
            throw error;
        }
    }

    async getReasonCodes(): Promise<ReasonCode[]> {
        try {
            const response = await this.apiService.get<ReasonCode[]>(
                `${API_BASE}/reason-codes-and-remarks`,
                { requireAuth: true }
            );
            
            return response.data;
        } catch (error) {
            this.flightChangeCaseErrorHandler.handle(error);
            throw error;
        }
    }

    async updateFlightCase(id: string, payload: UpdateFlightCasePayload): Promise<UpdateFlightCaseResponse> {
        try {
            const response = await this.apiService.patch<UpdateFlightCaseResponse>(
                `${API_BASE}${FLIGHT_CHANGE_CASES_ENDPOINT}/${id}`,
                payload,
                { requireAuth: true }
            );
            return response.data || {};
        } catch (error) {
            this.flightChangeCaseErrorHandler.handle(error);
            throw error;
        }
    }

    async assignFlightCase(id: string): Promise<void> {
        try {
            await this.apiService.patch(
                `${API_BASE}${FLIGHT_CHANGE_CASES_ENDPOINT}/${id}/assign`,
                undefined,
                { requireAuth: true }
            );
        } catch (error) {
            this.flightChangeCaseErrorHandler.handle(error);
            throw error;
        }
    }
}

